/**
 * @ngdoc directive
 * @module components.pagination
 * @name components.pagination:pagination
 * @scope
 * @restrict E
 * @description
 * The pagination component 
 * @param {string=} service String that refers to an ngResource stored in a factory. Should be in the form of "factory.resource." This resource will be called each time the filters change and when a new page is loaded. Should **not** be used together with the **data** param.
 * @param {number=} take Controls the number of items to include in each page. Defaults to 15.
 * @param {array=} data Array to be split up into pages. Should **not** be used with the **service** param.
 * @param {number=} page Page number to start on. Defaults to 1.
 * @param {string=} required Comma separated list of parameters that required. Delays the initial pagination load until each parameter has a value set. Used to avoid unnessary api calls.
 * @param {function=} onChange Callback function for when the page/filter is changed. Passes the following fields to the function.
 * - page: the current page
 * - newItems: the items that were just loaded
 * - items: all items that have been loaded for the current set of filters
 * - rows: the total number of items that can be loaded
 * - finished: flag indicating whether all items have been loaded
 * - empty: flag indicating that no items were found
 * @example
    <example module="dataExample">
        <file name="index.html">
            <div ng-controller="exampleController">
                <pagination data="exampleData">
                    <ul>
                        <li ng-repeat="item in $ctrl.items">{{item}}</li>
                    </ul>
                </pagination>
            </div>
        </file>
        <file name="script.js">
            (function() {
                angular.module('dataExample', ['components.pagination'])
                    .controller('exampleController', exampleController);

                exampleController.$inject = ['$scope'];
                function exampleController($scope) {
                    var data = [];
                    for (var i=0; i<25; i++) {
                        data.push('example ' + i);
                    }
                    $scope.exampleData = data;
                }
            })();
        </file>
    </example>
 */

export default function PaginationComponent(module) {
    angular.module(module || 'app')
        .component('pagination', {
            bindings: {
                service: '@?',
                take: '<?',
                data: '<?',
                page: '<?',
                params: '<?',
                requiredParams: '@?',
                scrollContainer: '@?',
                disableScroll: '<?',
                container: '@?',
                onChange: '&?',
                onInit: '&?',
                onError: '&?',
                onFound: '&?'
            },
            require: {
                stickyManager: '^?stickyManager'
            },
            transclude: true,
            controller: paginationController
        });
    paginationController.$inject = ['$scope', '$element', '$transclude', '$injector', '$q', '$window', '$timeout'];
    function paginationController($scope, $element, $transclude, $injector, $q, $window, $timeout) {
        var ctrl = this;
        ctrl.loading = false;
        ctrl.$onInit = function () {
            ctrl.$scroller = angular.element(!ctrl.scrollContainer ? $window : ctrl.scrollContainer);
            $timeout(function () {
                ctrl.$container = ctrl.container ? angular.element(ctrl.container) : $element;
            });
            if (!ctrl.take)
                ctrl.take = 15;
            if (!ctrl.page)
                ctrl.page = 1;
            if (!ctrl.$service && ctrl.data) {
                ctrl.rows = ctrl.data.length;
                ctrl.pageLimit = Math.ceil(ctrl.rows / ctrl.take);
            }
            else if (ctrl.onError) {
                ctrl.onError({ message: 'No content has been connected.' });
            }
            ctrl.$$initialized = true;
            if (ctrl.onInit) {
                ctrl.onInit({ ctrl: ctrl });
            }
        };

        ctrl.$onChanges = function (changes) {
            if (!ctrl._transcluded) {
                ctrl._injectedItems = [];
                $transclude($scope, function (clone) {
                    if (clone.length) {
                        //var injectedItems = clone.find('injected-items');
                        //if (injectedItems.length) {
                        //    clone.remove(injectedItems);
                        //}
                        var injectedItems = clone.filter('injected-items');
                        if (injectedItems.length) {
                            var items = injectedItems.find('>item');
                            ctrl.params.reservedIndexes = [];
                            for (var i = 0; i < items.length; i++) {
                                var item = angular.element(items[i]);
                                var index = parseInt(item.attr('index'));
                                ctrl.params.reservedIndexes.push(index);
                                ctrl._injectedItems.push({
                                    id: 'injected-' + i,
                                    index: index,
                                    markup: item.html(),
                                    _isInjected: true
                                })
                            }
                        }
                        $element.append(clone.filter(':not(injected-items)'));
                    }
                });
                ctrl._transcluded = true;
            }
            if (changes.service) {
                if (changes.service.currentValue) {
                    var servicePieces = changes.service.currentValue.split('.');
                    var serviceContainer = $injector.get(servicePieces[0]);
                    ctrl.$resource = serviceContainer[servicePieces[1]];
                }
                else {
                    ctrl.$resource = null;
                }
            }
            if (changes.params && changes.params.currentValue) {
                if (ctrl.$$paramsValid(changes.params.currentValue) && ctrl._paramsChanged(changes.params.currentValue, changes.params.previousValue)) {
                    ctrl.$$toPage(!changes.params.previousValue || changes.params.previousValue.page === undefined ? ctrl.page : 1, true, changes.params.currentValue);
                }
                else if (ctrl.onError) {
                    ctrl.onError({ message: 'Required parameter is missing.' });
                }
            }
        };

        ctrl._paramsChanged = function (newValue, oldValue) {
            if (newValue && oldValue) {
                var newKeys = Object.keys(newValue);
                var oldKeys = Object.keys(oldValue);
                if (newKeys.length !== oldKeys.length) {
                    return true;
                }
                for (var i = 0; i < newKeys.length; i++) {
                    var key = newKeys[i];
                    if (typeof (newValue[key]) === 'object') {
                        if (JSON.stringify(newValue[key]) !== JSON.stringify(oldValue[key])) {
                            return true;
                        }
                    }
                    else if (newValue[key] !== oldValue[key]) {
                        return true;
                    }
                }
            }
            else {
                return true;
            }
            return false;
        }

        //$scope.$watch('$ctrl.params', function (newVal, prevVal) {
        //    if (newVal !== undefined && !angular.equals(newVal, prevVal)) {
        //        //console.info('DEBUG: changes.params' + changes.params);
        //        ctrl.params = newVal;
        //        if (ctrl.$$paramsValid()) {
        //            ctrl.$$toPage(1, true);
        //        }
        //        else if (ctrl.onError) {
        //            ctrl.onError({ message: 'Required parameter is missing.' })
        //        }
        //    }
        //}, true);
        //ctrl.$onChanges = function (changes) {
        //    if (changes.params && !angular.equals(changes.params.currentValue, changes.params.previousValue)) {
        //        //console.info('DEBUG: changes.params' + changes.params);
        //        if (ctrl.$$paramsValid()) {
        //            ctrl.$$toPage(1, true);
        //        }
        //        else if (ctrl.onError) {
        //            ctrl.onError({ message: 'Required parameter is missing.' })
        //        }
        //    }
        //}

        ctrl.$postLink = function () {
            ctrl.$isInfinite = false;
            ctrl.scrollTarget = angular.element('[pagination-scroll]');
            if (!ctrl.scrollTarget.length) {
                ctrl.scrollTarget = angular.element('body,html');
            }
            if (ctrl.$isInfinite) {
                ctrl.$scroller.on('scroll.pagination', function () {
                    var boundingBox = $element[0].getBoundingClientRect();
                    if (ctrl.$scroller.height() >= boundingBox.bottom - 500) {
                        ctrl.nextPage();
                    }
                });
            }
        };

        ctrl.$onDestroy = function () {
            if (ctrl.$scroller) {
                ctrl.$scroller.off('scroll.pagination');
            }
        };

        /**
         * @ngdoc method
         * @name pagination#updateParam
         * @methodOf components.pagination:pagination
         * @kind function
         * @description
         * Updates a param passed to the pagination service. Only works for service driven pagination.
         * @param {string} key The name of the parameter that will be passed to the api call.
         * @param {object} value The value of the parameter.
         */
        ctrl.updateParam = function (key, value) {
            //console.info('DEBUG: updateParam');
            //console.info(ctrl.params);
            if (!ctrl.params)
                ctrl.params = {};
            ctrl.params[key] = value;
            ctrl.$$toPage(1, true);
        };

        /**
         * @ngdoc method
         * @name pagination#$$paramsValid
         * @methodOf components.pagination:pagination
         * @kind function
         * @description
         * Internal method used to check whether a value has been set for every required parameter. Used to delay service requests until all of the parameters have been populated.
         * @return {bool} True if all required parameters have a value.
         */
        ctrl.$$paramsValid = function (params) {
            params = params || ctrl.params;
            if (!ctrl.requiredParams)
                return true;
            if (!params) {
                return false;
            }
            var requirements = ctrl.requiredParams.replace(' ', '').split(',');
            for (var i = 0; i < requirements.length; i++) {
                var target = params[requirements[i]];
                if (target === undefined || target === null)
                    return false;
            }
            
            return true;
        };



        /**
         * @ngdoc method
         * @name pagination#getParam
         * @methodOf components.pagination:pagination
         * @kind function
         * @description
         * Gets the current value of a filter parameter.
         * @param {string} param The key for the filter parameter.
         * @return {object} The value of the requested parameter. Returns undefined if no value has been set.
         */
        ctrl.getParam = function (param) {
            if (ctrl.params)
                return ctrl.params[param];
            return undefined;
        };

        /**
         * @ngdoc method
         * @name pagination#prevPage
         * @methodOf components.pagination:pagination
         * @kind function
         * @description
         * Loads the items for the previous page of content.
         * @param {bool=} param If true, the newly loaded content will replace the current content.
         */
        ctrl.prevPage = function (refresh) {
            if (ctrl.page > 1 && !ctrl.loading) {
                ctrl.$$toPage(ctrl.page - 1, refresh);
            }
        };

        /**
         * @ngdoc method
         * @name pagination#nextPage
         * @methodOf components.pagination:pagination
         * @kind function
         * @description
         * Loads the items for the next page of content.
         * @param {bool=} param If true, the newly loaded content will replace the current content.
         */
        ctrl.nextPage = function (refresh) {
            if (!ctrl.finished && !ctrl.loading) {
                ctrl.$$toPage(ctrl.page + 1, refresh);
            }
        };

        /**
         * @ngdoc method
         * @name pagination#clearParam
         * @methodOf components.pagination:pagination
         * @kind function
         * @description
         * Used to remove a param from the pagination filtering. Primarily used to clear out search terms.
         * @param {string} param The filter parameter to be reset.
         */
        ctrl.clearParam = function (param) {
            ctrl.params[param] = null;
            ctrl.$$toPage(1, true);
        };

        /**
         * @ngdoc method
         * @name pagination#$$toPage
         * @methodOf components.pagination:pagination
         * @kind function
         * @description
         * Loads the content for a selected page.
         * @param {number} page The page to load content for.
         * @param {bool=} refresh Replace the existing list of content with the new list if true. Used when filter parameters change and when the pagination is navigable with buttons.
         */
        ctrl.$$toPage = function (page, refresh, params) {
            page = page || 1;
            var skip = (page - 1) * ctrl.take;
            var deferred = $q.defer();
            ctrl.$promise = deferred.promise;
            ctrl.loading = true;
            ctrl.empty = false;
            if (ctrl.$resource) {
                if (refresh && ctrl.initialized) {
                    ctrl.refreshing = true;
                    var stickPoint = ctrl.stickyManager ? ctrl.stickyManager.getStickPoint(1) : 0;
                    var top = $element[0].getBoundingClientRect().top;
                    if (top < stickPoint) {
                        ctrl.scrollTarget.stop().animate({
                            scrollTop: '+=' + (top - stickPoint)
                        }, 300);
                    }
                    else if (top >= ctrl.$scroller.height()) {
                        ctrl.scrollTarget.stop().animate({
                            scrollTop: '+=' + (top - stickPoint)
                        }, 300);
                    }
                }
                params = angular.copy(params || ctrl.params);
                params.pageSize = ctrl.take;
                params.page = page;
                ctrl.$resource(params).then(function (data) {
                    deferred.resolve(data);
                }).catch(function (error) {
                    deferred.reject(error);
                })
                /*ctrl.$resource.getCamel(params).$promise.then(function (data) {
                    deferred.resolve(data);
                }).catch(function (error) {
                    deferred.reject(error);
                })*/
            }
            else if (ctrl.data) {
                if (skip > ctrl.data.length - 1)
                    deferred.reject({ message: 'No content found for this page.' });
                else {
                    deferred.resolve({
                        items: ctrl.data.slice(skip, skip + ctrl.take),
                        rows: ctrl.data.length
                    });
                }
            }
            deferred.promise.then(function (response) {
                var data = response.data;
                if (refresh !== undefined && refresh) {
                    ctrl.items = data.items;
                    ctrl.$lastCheck = 0;
                }
                else {
                    ctrl.items = ctrl.items || [];
                    for (var i = 0; i < data.items.length; i++)
                        ctrl.items.push(data.items[i]);
                }

                ctrl.page = page;
                ctrl.count = data.count;
                ctrl.list = data.list;
                ctrl.labels = data.labels;
                ctrl.pageLimit = data.totalPages;
                ctrl.finished = ctrl.page === data.totalPages;
                ctrl.initialized = true;
                ctrl.empty = ctrl.items.length === 0;
                ctrl.loading = false;
                ctrl.refreshing = false;

                for (var i = 0; i < ctrl._injectedItems.length; i++) {
                    var item = ctrl._injectedItems[i];
                    if (ctrl.items.length >= item.index) {
                        if (!ctrl.items[item.index] || !ctrl.items[item.index]._isInjected) {
                            ctrl.items.splice(item.index, 0, item);
                        }
                    }
                    else if (ctrl.finished) {
                        ctrl.items.push(item);
                    }
                }
                $timeout(function () {
                    // var boundingBox = $element[0].getBoundingClientRect();
                    // if (ctrl.$isInfinite && !ctrl.finished && ctrl.$scroller.height() >= boundingBox.bottom - 500) {
                    //     ctrl.nextPage();
                    // }
                    if (ctrl.onChange) {
                        ctrl.onChange({
                            page: ctrl.page,
                            pageSize: ctrl.take,
                            newItems: data.items,
                            items: ctrl.items,
                            count: ctrl.count,
                            finished: ctrl.finished,
                            empty: ctrl.empty,
                            controller: ctrl
                        });
                    }
                });
            }).catch(function (error) {
                if (ctrl.onError)
                    ctrl.onError({ message: error.message });
            });
            return deferred.promise;
        };
    }
};